import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Printer, Edit, Trash2, X, Calendar, User, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { decryptId, encryptId } from '../utils/urlEncryption';
import SelectionPrintA4 from '../components/SelectionPrintA4';
import { openPrintWindow } from '../utils/printWindow';
import '../styles/print.css';

const SelectionDetail = ({ user }) => {
  const { selectionId: encryptedSelectionId } = useParams();
  const navigate = useNavigate();
  
  // Decrypt the selection ID from URL
  const selectionId = decryptId(encryptedSelectionId);
  
  const [selection, setSelection] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [expandedPaymentMode, setExpandedPaymentMode] = useState(null);
  const [printFormat, setPrintFormat] = useState(null);

  useEffect(() => {
    console.log('SelectionDetail loaded with ID:', selectionId);
    if (selectionId) {
      loadSelection();
    }
  }, [selectionId]);

  const loadSelection = async () => {
    try {
      console.log('Fetching selection:', selectionId);
      const [selectionRes, customersRes] = await Promise.all([
        axiosInstance.get(`/selections/${selectionId}`),
        axiosInstance.get(`/customers`)
      ]);
      
      console.log('Selection data loaded:', selectionRes.data);
      setSelection(selectionRes.data);
      
      const cust = customersRes.data.find(c => c.id === selectionRes.data.customer_id);
      setCustomer(cust);
    } catch (err) {
      console.error('Error loading selection:', err);
      toast.error('Failed to load selection');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('selection-a4-print-content');
    if (printContent) {
      openPrintWindow(printContent.outerHTML, `Selection ${selection?.selection_id} - A4 Print`, { pageSize: 'A4' });
    } else {
      toast.error('Print content not found. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/selections/${selectionId}`);
      toast.success('Selection deleted successfully');
      navigate('/selections');
    } catch (err) {
      console.error('Error deleting selection:', err);
      toast.error('Failed to delete selection');
    }
  };

  const handleEdit = () => {
    navigate(`/selections/${encryptId(selectionId)}/edit`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      Active: 'bg-green-100 text-green-700 border-green-300',
      Converted: 'bg-blue-100 text-blue-700 border-blue-300',
      Cancelled: 'bg-red-100 text-red-700 border-red-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!selection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4">
        <div className="text-center py-12">
          <p className="text-gray-500">Selection not found</p>
        </div>
      </div>
    );
  }

  // Calculate payment totals
  const paymentTotals = {
    totalAdvance: 0
  };

  if (selection.advance_payments) {
    selection.advance_payments.forEach(p => {
      const isRefund = p.transaction_type === 'REFUND';
      if (p.amount) {
        // Subtract if it's a refund, add if it's a payment
        paymentTotals.totalAdvance += isRefund ? -parseFloat(p.amount) : parseFloat(p.amount);
      }
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header - Hide on Print */}
      <div className="bg-white border-b-2 border-purple-200 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <button
                onClick={() => navigate('/selections')}
                className="p-2 hover:bg-purple-50 rounded-lg transition-colors touch-target flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl md:text-2xl font-bold text-gray-800 truncate">{selection.selection_id}</h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Customer Selection Details</p>
              </div>
            </div>
            <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold border-2 ${getStatusBadge(selection.status)} whitespace-nowrap flex-shrink-0 ml-2`}>
              {selection.status}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-colors text-xs sm:text-sm touch-target"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
            <button
              onClick={handleEdit}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-colors text-xs sm:text-sm touch-target"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
            {selection.status === 'Active' && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-xs sm:text-sm touch-target col-span-2 sm:col-span-1"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Print Header - Only visible on print */}
      <div className="hidden print:block text-center mb-6 border-b-2 pb-4">
        <h1 className="text-3xl font-bold text-gray-800">Customer Selection Receipt</h1>
        <p className="text-sm text-gray-500">Selection #{selection.selection_id}</p>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-3 sm:space-y-4">
        
        {/* Customer & Date Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* Customer Info */}
          <div className="bg-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 print:border-gray-300">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 print:text-gray-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Customer Information</h2>
            </div>
            {customer && (
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600 flex-shrink-0">Name:</span>
                  <span className="font-semibold text-right break-words">{customer.name}</span>
                </div>
                {customer.mobile && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Mobile:</span>
                    <span className="font-semibold">{customer.mobile}</span>
                  </div>
                )}
                {customer.alternate_mobile && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Alt Mobile:</span>
                    <span className="font-semibold">{customer.alternate_mobile}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Email:</span>
                    <span className="font-semibold text-xs break-all text-right">{customer.email}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selection Info */}
          <div className="bg-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 print:border-gray-300">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 print:text-gray-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Selection Information</h2>
            </div>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-gray-600 flex-shrink-0">Selection Date:</span>
                <span className="font-semibold">
                  {new Date(selection.selection_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-600 flex-shrink-0">Created By:</span>
                <span className="font-semibold text-right break-all">{selection.created_by}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-600 flex-shrink-0">Status:</span>
                <span className="font-semibold">{selection.status}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Item Photos */}
        {selection.photos && selection.photos.length > 0 && (
          <div className="bg-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 print:border-gray-300">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>📷</span> Item Photos ({selection.photos.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 print:grid-cols-2">
              {selection.photos.map((photo, index) => (
                <div
                  key={index}
                  className="relative group cursor-pointer print:cursor-default"
                  onClick={() => setPreviewImage(photo)}
                >
                  <img
                    src={photo}
                    alt={`Selection ${index + 1}`}
                    className="w-full h-32 sm:h-40 object-cover rounded-lg border-2 border-purple-200 hover:border-purple-400 transition-all print:h-32 print:border-gray-300"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg print:hidden"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Item Description & Remarks */}
        <div className="bg-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 print:border-gray-300">
          <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span>📝</span> Details
          </h2>
          
          {selection.items_description && (
            <div className="mb-3">
              <p className="text-xs text-gray-600 mb-1 font-semibold">Items Description:</p>
              <p className="text-xs sm:text-sm text-gray-800 bg-purple-50 p-2 sm:p-3 rounded-lg print:bg-gray-50 break-words">
                {selection.items_description}
              </p>
            </div>
          )}

          {selection.customer_remarks && (
            <div>
              <p className="text-xs text-gray-600 mb-1 font-semibold">Customer Remarks:</p>
              <p className="text-xs sm:text-sm text-gray-800 bg-yellow-50 p-2 sm:p-3 rounded-lg print:bg-gray-50 break-words">
                {selection.customer_remarks}
              </p>
            </div>
          )}

          {!selection.items_description && !selection.customer_remarks && (
            <p className="text-sm text-gray-500 italic">No description added</p>
          )}
        </div>

        {/* Rate Lock Info */}
        {selection.rate_policy === 'lock-today' && selection.base24k_rate_ex_gst && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg sm:rounded-xl p-3 sm:p-4 print:bg-white print:border-gray-300">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>💰</span> Rate Locked
            </h2>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700">24K Gold Rate:</span>
                <span className="text-xl font-bold text-green-700">
                  ₹{selection.base24k_rate_ex_gst.toLocaleString('en-IN')}/g
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>GST Style:</span>
                <span className="font-medium">{selection.gst_input_style === 'EXCL' ? 'Excluding GST' : 'Including GST'}</span>
              </div>
              {selection.rate_locked_date && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Locked On:</span>
                  <span className="font-medium">
                    {new Date(selection.rate_locked_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Advance Payment - Same as Order Module */}
        {selection.advance_payments && selection.advance_payments.length > 0 && (
          <div className="bg-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 print:border-gray-300">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 print:text-gray-600" />
              Advance Payment
            </h2>
            
            {/* Payment Summary by Mode - Same as Order Module */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-3 mb-3">
              <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                <span>📊</span> Payment Mode Summary
              </h3>
              <div className="space-y-1.5 text-xs sm:text-sm">
                {(() => {
                  // Calculate totals by payment mode
                  const modeTotals = {};
                  const paymentsByMode = {};
                  
                  selection.advance_payments.forEach(payment => {
                    const mode = payment.mode || 'Other';
                    const isRefund = payment.transaction_type === 'REFUND';
                    
                    if (!modeTotals[mode]) {
                      modeTotals[mode] = {
                        cashAmount: 0,
                        count: 0
                      };
                      paymentsByMode[mode] = [];
                    }
                    modeTotals[mode].count++;
                    paymentsByMode[mode].push(payment);
                    
                    if (payment.amount) {
                      const amount = parseFloat(payment.amount);
                      // Subtract refunds, add payments
                      modeTotals[mode].cashAmount += isRefund ? -amount : amount;
                    }
                  });
                  
                  // Sort modes for consistent display
                  const sortedModes = Object.keys(modeTotals).sort();
                  
                  return (
                    <>
                      {sortedModes.map(mode => {
                        const data = modeTotals[mode];
                        const isExpanded = expandedPaymentMode === mode;
                        
                        return (
                          <div key={mode}>
                            {/* Mode Summary Row - Clickable */}
                            <div className="flex justify-between items-center py-1.5 border-b border-green-100 last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-800">{mode}</span>
                                <button
                                  onClick={() => setExpandedPaymentMode(isExpanded ? null : mode)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer touch-target px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                                >
                                  ({data.count} transaction{data.count > 1 ? 's' : ''})
                                </button>
                              </div>
                              <div className="flex flex-col items-end">
                                {data.cashAmount !== 0 && (
                                  <span className={`font-bold ${data.cashAmount > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    {data.cashAmount > 0 ? '' : '-'}₹{Math.abs(data.cashAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Expanded Transaction Details for This Mode */}
                            {isExpanded && (
                              <div className="mt-2 mb-3 ml-2 space-y-2 border-l-2 border-blue-300 pl-3 animate-slide-down">
                                {paymentsByMode[mode].map((payment, idx) => {
                                  const isRefund = payment.transaction_type === 'REFUND';
                                  return (
                                    <div key={idx} className={`border rounded-lg p-3 shadow-sm ${isRefund ? 'bg-red-50 border-red-300' : 'bg-white border-purple-200'}`}>
                                      {/* Payment Header */}
                                      <div className="flex justify-between items-start pb-2 border-b border-gray-200">
                                        <div className="flex-1">
                                          <span className={`font-semibold text-xs ${isRefund ? 'text-red-700' : 'text-gray-800'}`}>
                                            {isRefund ? '↩️ REFUND' : 'Transaction'} #{idx + 1}
                                          </span>
                                          {payment.date && (
                                            <span className="text-xs text-gray-600 block">
                                              {new Date(payment.date).toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric'
                                              })}
                                            </span>
                                          )}
                                        </div>
                                        <span className={`font-bold text-sm ${isRefund ? 'text-red-700' : 'text-purple-700'}`}>
                                          {payment.amount ? `${isRefund ? '-' : ''}₹${parseFloat(payment.amount).toLocaleString('en-IN')}` : '-'}
                                        </span>
                                      </div>
                                      
                                      {/* Payment Details */}
                                      <div className="mt-2 space-y-1 text-xs">
                                        {payment.note && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Note:</span>
                                            <span className="font-medium text-gray-800">{payment.note}</span>
                                          </div>
                                        )}
                                        {payment.upi_ref && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">UPI Ref:</span>
                                            <span className="font-medium text-gray-800">{payment.upi_ref}</span>
                                          </div>
                                        )}
                                        {payment.card_last4 && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Card:</span>
                                            <span className="font-medium text-gray-800">**** {payment.card_last4}</span>
                                          </div>
                                        )}
                                        {payment.pos_ref && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">POS Ref:</span>
                                            <span className="font-medium text-gray-800">{payment.pos_ref}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
            
            {/* Total Advance */}
            <div className="border-t-2 border-purple-300 pt-2 print:border-gray-300">
              <div className="flex justify-between items-center gap-2">
                <span className="font-bold text-sm sm:text-base text-gray-800">Total Advance:</span>
                <span className={`text-lg sm:text-xl font-bold whitespace-nowrap ${paymentTotals.totalAdvance >= 0 ? 'text-purple-700 print:text-gray-800' : 'text-red-700'}`}>
                  {paymentTotals.totalAdvance >= 0 ? '' : '-'}₹{Math.abs(paymentTotals.totalAdvance).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Revision History */}
        {selection.revision_history && selection.revision_history.length > 0 && (
          <div className="bg-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 print:hidden">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>📝</span> 
              <span className="hidden sm:inline">Revision History ({selection.revision_history.length})</span>
              <span className="sm:hidden">Changes ({selection.revision_history.length})</span>
            </h2>
            <div className="space-y-3">
              {selection.revision_history.map((revision, index) => (
                <div key={index} className="bg-purple-50 rounded-lg p-2 sm:p-3 border border-purple-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
                    <div className="flex-1">
                      <span className="font-semibold text-xs sm:text-sm text-purple-700">
                        Revision #{revision.revision_number}
                      </span>
                      <p className="text-xs text-gray-600 mt-0.5">
                        <span className="hidden sm:inline">By {revision.user} on </span>
                        <span className="sm:hidden">{revision.user} • </span>
                        {new Date(revision.timestamp).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {revision.changes.map((change, changeIndex) => {
                      // Special handling for photos array
                      const isPhotosField = change.field === 'photos';
                      const oldPhotos = isPhotosField && Array.isArray(change.old_value) ? change.old_value : [];
                      const newPhotos = isPhotosField && Array.isArray(change.new_value) ? change.new_value : [];
                      
                      return (
                        <div key={changeIndex} className="text-xs bg-white p-2 rounded border border-purple-100">
                          <div className="font-semibold text-gray-700 mb-1 text-xs sm:text-sm break-words">
                            {change.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </div>
                          {isPhotosField ? (
                            // Special display for photos with thumbnails
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-red-600">{oldPhotos.length} photo{oldPhotos.length !== 1 ? 's' : ''}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-green-600 font-semibold">{newPhotos.length} photo{newPhotos.length !== 1 ? 's' : ''}</span>
                              </div>
                              {/* Show photo thumbnails */}
                              <div className="grid grid-cols-2 gap-2">
                                {/* Before photos */}
                                {oldPhotos.length > 0 && (
                                  <div>
                                    <p className="text-xs text-red-600 font-semibold mb-1">Before:</p>
                                    <div className="grid grid-cols-2 gap-1">
                                      {oldPhotos.slice(0, 4).map((photo, i) => (
                                        <img
                                          key={i}
                                          src={photo}
                                          alt={`Before ${i+1}`}
                                          className="w-full h-16 object-cover rounded border border-red-300"
                                          onClick={() => setPreviewImage(photo)}
                                        />
                                      ))}
                                      {oldPhotos.length > 4 && (
                                        <div className="w-full h-16 bg-gray-200 rounded border border-red-300 flex items-center justify-center text-xs text-gray-600">
                                          +{oldPhotos.length - 4}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {/* After photos */}
                                {newPhotos.length > 0 && (
                                  <div>
                                    <p className="text-xs text-green-600 font-semibold mb-1">After:</p>
                                    <div className="grid grid-cols-2 gap-1">
                                      {newPhotos.slice(0, 4).map((photo, i) => (
                                        <img
                                          key={i}
                                          src={photo}
                                          alt={`After ${i+1}`}
                                          className="w-full h-16 object-cover rounded border border-green-300"
                                          onClick={() => setPreviewImage(photo)}
                                        />
                                      ))}
                                      {newPhotos.length > 4 && (
                                        <div className="w-full h-16 bg-gray-200 rounded border border-green-300 flex items-center justify-center text-xs text-gray-600">
                                          +{newPhotos.length - 4}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
                              <span className="text-red-600 line-through text-xs break-all max-w-full">
                                {typeof change.old_value === 'string' && change.old_value.length > 50 
                                  ? `${change.old_value.substring(0, 50)}...` 
                                  : JSON.stringify(change.old_value)}
                              </span>
                              <span className="text-gray-400 text-xs">→</span>
                              <span className="text-green-600 font-semibold text-xs break-all max-w-full">
                                {typeof change.new_value === 'string' && change.new_value.length > 50 
                                  ? `${change.new_value.substring(0, 50)}...` 
                                  : JSON.stringify(change.new_value)}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Print Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t-2 border-gray-300 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Please visit us to finalize your order.
          </p>
          <p className="text-xs text-gray-500">
            Thank you!
          </p>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Selection?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this selection? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 print:hidden"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100"
          >
            <X className="w-6 h-6 text-gray-800" />
          </button>
          <div className="max-w-4xl max-h-screen">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-screen object-contain"
            />
          </div>
        </div>
      )}

      {/* A4 Print Component */}
      {printFormat === 'a4' && (
        <SelectionPrintA4 selection={selection} customer={customer} />
      )}
    </div>
  );
};

export default SelectionDetail;
